import Decimal from 'decimal.js'

import { IgnoredXeroServiceCodes } from 'constants/index'
import {
  GetBookingQuery,
  GetUserBookingQuery,
  GoogleAddressDtoInput,
  XeroServiceType,
} from 'generated/graphql'
import {
  TBookingAddressForm,
  TBookingChargeForm,
  TBookingForm,
  TBookingPackageForm,
} from 'types/form-types'

interface IDriver {
  charge: number
  extraPayment: number
}

export const getTotalDriverCharge = (drivers: Array<IDriver> | undefined) => {
  if (!drivers) {
    return 0
  }

  if (drivers.length < 1) {
    return 0
  }

  return drivers.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.charge + currentValue.extraPayment,
    0,
  )
}

interface ICharge {
  rate: number
  quantity: number
}

export const getOtherChargesValue = (charges: Array<ICharge> | undefined | null) => {
  if (!charges) {
    return 0
  }
  return charges.reduce(
    (previousValue, currentValue) => currentValue.rate * currentValue.quantity + previousValue,
    0,
  )
}

export const getBookingRevenue = (consignmentFee: number, otherCharges: number) =>
  new Decimal(consignmentFee).plus(otherCharges).toNumber()

export const getBookingMargin = (revenue: number, totalDriverCharge: number) => {
  if (revenue === 0 && totalDriverCharge === 0) {
    return 0
  }

  return new Decimal(revenue).minus(totalDriverCharge).div(revenue).toNumber()
}

export const getGoogleAddressInput = (
  collection: Array<any>,
  delivery: Array<any>,
): Array<GoogleAddressDtoInput> =>
  [...collection, ...delivery]
    .map((address) => {
      if (address.name || address.city || address.addressLine1 || address.postcode) {
        return {
          sequenceOrder: address.sequenceOrder,
          addressLine1: address.addressLine1 || '',
          ...(address.addressLine2 && {
            addressLine2: address.addressLine2,
          }),
          postcode: address.postcode,
          city: address.city || '',
          countryId: address.countryId,
        }
      }
    })
    .filter((address): address is GoogleAddressDtoInput => !!address)

export const isMultiDrop = (bookingDetails: TBookingForm) =>
  bookingDetails.collectionAddresses.length + bookingDetails.deliveryAddresses.length !== 2
export const ignoreServiceType = (serviceType: XeroServiceType | undefined) =>
  !serviceType || IgnoredXeroServiceCodes.includes(serviceType)

export const mapToBookingAddressForm = (addressEntry: any): TBookingAddressForm => ({
  at: '',
  by: '',
  name: addressEntry.address.name,
  city: addressEntry.address.city,
  addressLine1: addressEntry.address.addressLine1,
  addressLine2: addressEntry.address.addressLine2 ?? '',
  postcode: addressEntry.address.postcode,
  countryId: Number(addressEntry.address.countryId),
  contact: addressEntry.address.contact ?? '',
  email: addressEntry.address.email ?? '',
  telephoneNumber: addressEntry.address.telephoneNumber ?? '',
  note: addressEntry.note,
  sequenceOrder: addressEntry.sequenceOrder,
  isVerified: addressEntry.address.isVerified,
  reasonCodeId: Number(addressEntry.reasonCodeId),
})

export const mapToBookingPackageForm = (pkg: any): TBookingPackageForm => ({
  quantity: pkg.quantity,
  packageType: pkg.type,
  collectionSequenceOrder: pkg.collectionSequenceOrder,
  deliverySequenceOrder: pkg.deliverySequenceOrder,
  length: pkg.length,
  width: pkg.width,
  height: pkg.height,
  weight: pkg.weight,
  reference: pkg.packageReference,
  details: pkg.name,
})

export const mapToBookingChargeForm = (charge: any): TBookingChargeForm => ({
  name: charge.name,
  type: charge.type,
  quantity: charge.quantity,
  rate: charge.rate,
  description: charge.description,
  xeroServiceCode: charge.xeroServiceCode,
  vatCategoryId: String(charge.vatCategoryId),
})

export const getWaitAndReturnFeeCategoryId = (
  booking:
    | NonNullable<GetBookingQuery['booking']>
    | NonNullable<GetUserBookingQuery['userBooking']>,
) => {
  const bookingValue = booking.waitAndReturnFeeCategoryId
  const customerValue = booking.customer?.waitAndReturnFeeCategoryId

  const currentValue =
    (bookingValue == null || bookingValue === 1) && customerValue != null
      ? customerValue
      : bookingValue

  return currentValue != null ? String(currentValue) : ''
}
